import { CreateUserRequest, UpdateUserGroupRequest, User, UserGroupRepresentation } from '@common/api';
import { usersApi, userGroupsApi } from '@data/rest/api';
import { QueryClient, QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addQueryClientArrayMember, removeQueryClientArrayMember, updateQueryClientArrayMember } from '@util/query';

function getResourcesAndTarget<TResource extends { id: number }>(
    queryClient: QueryClient,
    targetId: number,
    queryKey: QueryKey
): [TResource | undefined, TResource[]] {
    const resources = queryClient.getQueryData(queryKey) as TResource[];
    const target = resources.find(({ id }) => id === targetId);

    return [target, [...resources]];
}

function removeCachedEntityById<TResource extends { id: number }>(
    queryClient: QueryClient,
    targetId: number,
    queryKey: QueryKey
) {
    const resources = queryClient.getQueryData(queryKey) as TResource[];
    const filtered = resources.filter(({ id }) => id !== targetId) ?? [];
    queryClient.setQueryData(queryKey, filtered);
}

/**
 *
 * @returns the list of all users
 */
export function useUsers<TSelectData = User[]>(options?: { select: (data: User[]) => TSelectData; enabled: boolean }) {
    const { data } = useQuery(['users'], () => usersApi.list(), { ...options });
    if (!data) {
        return [];
    }
    return data;
}
export function useUsers2<TSelectData = User[]>(options?: { select: (data: User[]) => TSelectData; enabled: boolean }) {
    const users = useQuery(['users'], () => usersApi.list(), { ...options });
    return users;
}

export function useGroups() {
    const { data } = useQuery<UserGroupRepresentation[]>(['groups'], () => userGroupsApi.list());
    if (!data) {
        return [];
    }
    return data;
}

export function useCreateGroupAction() {
    const queryClient = useQueryClient();
    const mutation = useMutation((userGroup: Omit<UserGroupRepresentation, 'id'>) => userGroupsApi.create(userGroup), {
        onSettled: async () => {
            queryClient.invalidateQueries(['groups']);
        },
    });

    return function createGroup(userGroup: Omit<UserGroupRepresentation, 'id'>) {
        mutation.mutate(userGroup);
    };
}

export function useRemoveGroupAction() {
    const queryClient = useQueryClient();
    const mutation = useMutation((userGroup: UserGroupRepresentation) => userGroupsApi.remove(userGroup.id), {
        onSettled: async () => {
            queryClient.invalidateQueries(['groups']);
        },
    });

    return function removeGroup(userGroup: UserGroupRepresentation) {
        mutation.mutate(userGroup);
    };
}

interface UserIds {
    userIds: number[];
}

export function useUserGroupActions() {
    const queryClient = useQueryClient();

    const removeMutation = useMutation((id: number) => userGroupsApi.remove(id), {
        async onMutate(id) {
            await queryClient.cancelQueries(['groups']);
            removeCachedEntityById<UserGroupRepresentation>(queryClient, id, ['groups']);
        },
        async onSettled() {
            queryClient.invalidateQueries(['groups']);
        },
    });

    const createMutation = useMutation((name: string) => userGroupsApi.create({ name }), {
        async onMutate(name) {
            await queryClient.cancelQueries(['groups']);
            const groups = queryClient.getQueryData<UserGroupRepresentation[]>(['groups']);
            if (groups) queryClient.setQueryData(['groups'], [{ id: 0, name, userIds: [] }, ...groups]);
        },
        async onSettled() {
            queryClient.invalidateQueries(['groups']);
        },
    });

    const addUserMutation = useMutation(
        (data: { payload: UserIds; targetId: number }) => userGroupsApi.addUsers(data.targetId, data.payload),
        {
            onMutate: async (data) => {
                await queryClient.cancelQueries(['groups']);
                const [group, groups] = getResourcesAndTarget<UserGroupRepresentation>(queryClient, data.targetId, [
                    'groups',
                ]);
                if (group) {
                    group.userIds = [...data.payload.userIds, ...group.userIds].sort((a, b) => b - a);
                    queryClient.setQueryData(['groups'], groups);
                }
            },
            onSettled: async () => {
                queryClient.invalidateQueries(['groups']);
            },
        }
    );

    const removeUserMutation = useMutation(
        (data: { payload: UserIds; targetId: number }) => userGroupsApi.removeUsers(data.targetId, data.payload),
        {
            onMutate: async (data: { payload: UserIds; targetId: number }) => {
                await queryClient.cancelQueries(['groups']);
                // eslint-disable-next-line prefer-const
                let [group, groups] = getResourcesAndTarget<UserGroupRepresentation>(queryClient, data.targetId, [
                    'groups',
                ]);
                if (group) {
                    queryClient.setQueryData(['groups'], [...groups]);
                }
            },
            onSettled: async () => {
                queryClient.invalidateQueries(['groups']);
            },
        }
    );

    const mutation = useMutation(
        (data: { updateData: UpdateUserGroupRequest; id: number }) => userGroupsApi.update(data.id, data.updateData),
        {
            onMutate: async (data) => {
                await queryClient.cancelQueries(['groups']);
                const [group, groups] = getResourcesAndTarget<UserGroupRepresentation>(queryClient, data.id, [
                    'groups',
                ]);
                if (group) {
                    queryClient.setQueryData(['groups'], groups);
                }
            },
            onSettled: async () => {
                queryClient.invalidateQueries(['groups']);
            },
        }
    );

    return {
        createGroup(name: string) {
            createMutation.mutate(name);
        },
        addUserToGroup: function (userId: number, groupId: number) {
            addUserMutation.mutate({ payload: { userIds: [userId] }, targetId: groupId });
        },
        removeUserFromGroup: function (userId: number, groupId: number) {
            removeUserMutation.mutate({ payload: { userIds: [userId] }, targetId: groupId });
        },
        updateGroup: function (id: number, updateData: UpdateUserGroupRequest) {
            mutation.mutate({ id, updateData });
        },
        removeGroup(id: number) {
            removeMutation.mutate(id);
        },
    };
}

export function useUserActions() {
    const queryClient = useQueryClient();

    const createMutation = useMutation((user: CreateUserRequest) => usersApi.create(user), {
        onMutate: async () => {
            await queryClient.cancelQueries(['users']);
        },
        onSettled: async (user) => {
            if (user) {
                addQueryClientArrayMember(queryClient, ['users'], user);
            }
            queryClient.invalidateQueries(['users']);
            queryClient.invalidateQueries(['groups']);
        },
    });

    const updateMutation = useMutation((user: User) => usersApi.update(user.id, user), {
        onMutate: async (user) => {
            await queryClient.cancelQueries(['users']);
            updateQueryClientArrayMember(queryClient, ['users'], user);
        },
        onSettled: async () => {
            queryClient.invalidateQueries(['users']);
            // Also invalidate current user query to update avatar in sidebar etc.
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey.includes('currentUser'),
            });
        },
    });

    const deleteMutation = useMutation((userId: number) => usersApi.remove(userId), {
        onMutate: async (userId) => {
            await queryClient.cancelQueries(['users']);
            removeQueryClientArrayMember(queryClient, ['users'], userId);
        },
        onSettled: async () => {
            queryClient.invalidateQueries(['users']);
        },
    });

    return {
        createUser: function createUser(user: CreateUserRequest) {
            createMutation.mutate(user);
        },
        deleteUser: function deleteUser(user: User) {
            deleteMutation.mutate(user.id);
        },
        updateUser: function editUser(user: User) {
            updateMutation.mutate(user);
        },
        isUpdating: updateMutation.isLoading,
    };
}
